'use client';

import { useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/shared/components/Button';
import VideoCarousel from '@/shared/components/VideoCarousel';
import { useAnalytics } from '@/core/analytics';

export default function FeaturesPage() {
	const analytics = useAnalytics();

	useEffect(() => {
		analytics.logEvent('features_page_visited');
	}, []);

	return (
		<div className="min-h-screen overflow-hidden">
			{/* Fullscreen Video Background */}
			<div className="fixed inset-0 z-0">
				<VideoCarousel />
			</div>

			{/* Navigation */}
			<nav className="glass-dark fixed top-0 left-0 right-0 z-50 border-b border-white/10">
				<div className="container mx-auto px-4 py-4 flex justify-between items-center">
					<Link href="/" className="flex items-center">
						<Image
							src="/ts-logo2-v2.png"
							alt="TabStudio Logo"
							width={160}
							height={53}
							className="h-12 w-auto"
							loading="lazy"
							unoptimized
						/>
					</Link>
					<div className="flex items-center gap-4">
						<Link href="/login" prefetch={false}>
							<Button
								variant="ghost"
								size="sm"
								className="glass-light glass-hover text-white border-white/20"
							>
								Sign In
							</Button>
						</Link>
						<Link href="/register" prefetch={false}>
							<Button
								variant="default"
								size="sm"
								className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
							>
								Get Started
							</Button>
						</Link>
					</div>
				</div>
			</nav>

			<main className="relative z-10 pt-24">
				{/* Hero Section */}
				<section className="py-24 px-4">
					<div className="container mx-auto text-center">
						<h1 className="text-5xl md:text-7xl font-bold mb-6">
							Powerful Features for
							<br />
							<span className="bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">
								Creative Excellence
							</span>
						</h1>
						<p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
							Everything you need to create professional
							AI-generated videos. From text-to-video to advanced
							editing tools.
						</p>
					</div>
				</section>

				{/* AI Generation Features */}
				<section className="py-16 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							AI Generation
						</h2>
						<div className="grid gap-8 md:grid-cols-2 max-w-6xl mx-auto">
							<div className="glass-gradient p-8 rounded-2xl border border-white/20">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
										/>
									</svg>
								</div>
								<h3 className="text-2xl font-semibold mb-3">
									Text to Video
								</h3>
								<p className="text-muted-foreground mb-4">
									Transform your written descriptions into
									high-quality videos. Simply describe your
									vision and watch AI bring it to life with
									stunning visuals and smooth motion.
								</p>
								<ul className="space-y-2 text-sm text-muted-foreground">
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-primary"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										Natural language processing
									</li>
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-primary"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										Multiple duration options
									</li>
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-primary"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										Custom aspect ratios
									</li>
								</ul>
							</div>

							<div className="glass-gradient p-8 rounded-2xl border border-white/20">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-secondary to-secondary-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
										/>
									</svg>
								</div>
								<h3 className="text-2xl font-semibold mb-3">
									Image to Video
								</h3>
								<p className="text-muted-foreground mb-4">
									Create smooth video transitions between
									keyframes. Upload images and AI generates
									seamless motion between them with natural
									movement and transitions.
								</p>
								<ul className="space-y-2 text-sm text-muted-foreground">
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-secondary"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										First & last frame control
									</li>
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-secondary"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										Smooth interpolation
									</li>
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-secondary"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										High-quality output
									</li>
								</ul>
							</div>

							<div className="glass-gradient p-8 rounded-2xl border border-white/20">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-accent to-accent-dark rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
										/>
									</svg>
								</div>
								<h3 className="text-2xl font-semibold mb-3">
									Scene Editor
								</h3>
								<p className="text-muted-foreground mb-4">
									Professional timeline-based editor for
									creating complex video sequences. Arrange,
									trim, and combine multiple scenes into a
									cohesive story.
								</p>
								<ul className="space-y-2 text-sm text-muted-foreground">
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-accent"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										Drag-and-drop timeline
									</li>
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-accent"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										Multi-track editing
									</li>
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-accent"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										Real-time preview
									</li>
								</ul>
							</div>

							<div className="glass-gradient p-8 rounded-2xl border border-white/20">
								<div className="w-12 h-12 mb-6 bg-gradient-to-br from-primary-dark to-secondary rounded-xl flex items-center justify-center">
									<svg
										className="w-6 h-6 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											strokeWidth={2}
											d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
										/>
									</svg>
								</div>
								<h3 className="text-2xl font-semibold mb-3">
									Text to Image
								</h3>
								<p className="text-muted-foreground mb-4">
									Generate stunning images from text
									descriptions. Perfect for creating reference
									images, storyboards, or standalone artwork
									for your projects.
								</p>
								<ul className="space-y-2 text-sm text-muted-foreground">
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-primary"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										Multiple models
									</li>
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-primary"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										Reference image support
									</li>
									<li className="flex items-center gap-2">
										<svg
											className="w-4 h-4 text-primary"
											fill="currentColor"
											viewBox="0 0 20 20"
										>
											<path
												fillRule="evenodd"
												d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
												clipRule="evenodd"
											/>
										</svg>
										Batch generation
									</li>
								</ul>
							</div>
						</div>
					</div>
				</section>

				{/* Collaboration Features */}
				<section className="py-16 px-4">
					<div className="container mx-auto">
						<h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
							Collaboration & Management
						</h2>
						<div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto">
							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all">
								<h3 className="text-xl font-semibold mb-3">
									Asset Library
								</h3>
								<p className="text-muted-foreground">
									Centralized asset management with support
									for images, videos, and generated content.
									Easily organize and reuse assets across
									projects.
								</p>
							</div>
							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all">
								<h3 className="text-xl font-semibold mb-3">
									Team Workspaces
								</h3>
								<p className="text-muted-foreground">
									Create organizations, invite team members,
									and collaborate in real-time. Share projects
									and assets with granular permissions.
								</p>
							</div>
							<div className="p-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm hover:bg-white/10 transition-all">
								<h3 className="text-xl font-semibold mb-3">
									Project Management
								</h3>
								<p className="text-muted-foreground">
									Organize your work with projects. Keep track
									of all your videos, assets, and edits in one
									structured workspace.
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* CTA Section */}
				<section className="py-24 px-4 bg-card/30 backdrop-blur-sm">
					<div className="container mx-auto">
						<div className="glass-gradient glass-hover relative max-w-4xl mx-auto text-center p-12 rounded-3xl border border-white/20 overflow-hidden shadow-2xl">
							<div className="relative z-10">
								<h2 className="text-4xl md:text-5xl font-bold mb-6">
									Experience All Features
								</h2>
								<p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
									Start creating with all our powerful
									features today. No credit card required.
								</p>
								<Link href="/register" prefetch={false}>
									<Button
										variant="default"
										size="lg"
										className="glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30"
									>
										Get Started Free
									</Button>
								</Link>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* Footer */}
			<footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm">
				<div className="container mx-auto px-4 py-12">
					<div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
						<div>
							<h4 className="font-semibold mb-4">Product</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/features">Features</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Company</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/about">About</Link>
								</li>
								<li>
									<Link href="/careers">Careers</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Resources</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/help">Help Center</Link>
								</li>
								<li>
									<Link href="/api">API</Link>
								</li>
							</ul>
						</div>
						<div>
							<h4 className="font-semibold mb-4">Legal</h4>
							<ul className="space-y-2 text-sm text-muted-foreground">
								<li>
									<Link href="/privacy">Privacy</Link>
								</li>
								<li>
									<Link href="/terms">Terms</Link>
								</li>
								<li>
									<Link href="/security">Security</Link>
								</li>
							</ul>
						</div>
					</div>
					<div className="pt-8 border-t border-border/50 text-center text-sm text-muted-foreground">
						<p>
							&copy; 2025 TabStudio. Powered by AI. Built for
							creators.
						</p>
					</div>
				</div>
			</footer>
		</div>
	);
}
